// @ts-nocheck
import { getSpeechRecognitionCtor } from "./speechRecognition";

const defaultLog = (label, detail) => {
  if (detail !== undefined) {
    // eslint-disable-next-line no-console
    // console.log(`[TRANSCRIPTION] ${label}`, detail);
  } else {
    // eslint-disable-next-line no-console
    // console.log(`[TRANSCRIPTION] ${label}`);
  }
};

export function createTranscriptionService({
  getCtor = getSpeechRecognitionCtor,
  lang = "en-IN",
  shouldRestart = () => false,
  onInterim = () => {},
  onFinal = () => {},
  onError = () => {},
  onStatus = () => {},
  log = defaultLog,
  restartDelayMs = 300,
  maxRestartDelayMs = 3000,
  silenceTimeoutMs = 15000,
} = {}) {
  let recognition = null;
  let running = false;
  let manualStop = false;
  let restartTimer = null;
  let watchdogTimer = null;
  let restartAttempts = 0;
  let sessionId = 0;

  function clearRestartTimer() {
    if (restartTimer) {
      clearTimeout(restartTimer);
      restartTimer = null;
    }
  }
  function clearWatchdog() {
    if (watchdogTimer) {
      clearTimeout(watchdogTimer);
      watchdogTimer = null;
    }
  }
  function armWatchdog() {
    clearWatchdog();
    if (!silenceTimeoutMs) return;
    watchdogTimer = setTimeout(() => {
      watchdogTimer = null;
      if (manualStop || !shouldRestart()) return;
      log("Watchdog Restart", `no result for ${silenceTimeoutMs}ms`);
      forceRestart();
    }, silenceTimeoutMs);
  }
  function scheduleRestart() {
    clearRestartTimer();
    const delay = Math.min(
      restartDelayMs * Math.pow(2, restartAttempts),
      maxRestartDelayMs
    );
    restartAttempts += 1;
    restartTimer = setTimeout(() => {
      restartTimer = null;
      if (!manualStop && shouldRestart()) start();
    }, delay);
  }
  function detach(r) {
    if (!r) return;
    r.onresult = null;
    r.onend = null;
    r.onerror = null;
  }
  function forceRestart() {
    const old = recognition;
    detach(old);
    recognition = null;
    running = false;
    if (old) {
      try {
        if (typeof old.abort === "function") old.abort();
        else old.stop();
      } catch (_e) {
        // ignore
      }
    }
    scheduleRestart();
  }

  function handleResult(event) {
    restartAttempts = 0;
    armWatchdog();
    let interim = "";
    for (let i = event.resultIndex; i < event.results.length; i += 1) {
      const res = event.results[i];
      const text = (res && res[0] && res[0].transcript) || "";
      if (res.isFinal) {
        const finalText = text.trim();
        if (finalText) {
          log("Final Result", finalText);
          onFinal(finalText);
        }
      } else {
        interim += text;
      }
    }
    const trimmedInterim = interim.trim();
    if (trimmedInterim) {
      log("Interim Result", trimmedInterim);
      onInterim(trimmedInterim);
    }
  }

  function handleEnd() {
    running = false;
    clearWatchdog();
    if (manualStop) return; // stop() owns its own status emission
    if (shouldRestart()) {
      log("Restarting");
      scheduleRestart();
      return;
    }
    log("Stopped");
    onStatus("stopped");
  }

  function handleError(event) {
    const code = (event && event.error) || "unknown";
    log("Error", code);
    if (code !== "no-speech" && code !== "aborted") onError(code);
    // 'not-allowed' / 'audio-capture' won't recover via restart; onend handles the rest.
    if (code === "not-allowed" || code === "audio-capture") {
      manualStop = true;
    }
  }

  function start() {
    const Ctor = getCtor();
    if (!Ctor) {
      log("Browser Unsupported");
      onStatus("unsupported");
      return false;
    }
    if (running) return false; // guard duplicates / restart loops
    manualStop = false;
    log("Initializing");
    detach(recognition);
    sessionId += 1;
    const mySession = sessionId;
    const stale = () => mySession !== sessionId;
    recognition = new Ctor();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = lang;
    recognition.onresult = (e) => {
      if (stale()) return;
      handleResult(e);
    };
    recognition.onend = () => {
      if (stale()) return;
      handleEnd();
    };
    recognition.onerror = (e) => {
      if (stale()) return;
      handleError(e);
    };
    try {
      recognition.start();
      running = true;
      log("Started");
      onStatus("running");
      armWatchdog();
      return true;
    } catch (e) {
      running = false;
      detach(recognition);
      recognition = null;
      log("Start Failed", (e && e.name) || "unknown");
      if (!manualStop && shouldRestart()) {
        scheduleRestart();
        return false;
      }
      onError("start-failed");
      onStatus("stopped");
      return false;
    }
  }

  function stop() {
    manualStop = true;
    clearRestartTimer();
    clearWatchdog();
    running = false;
    sessionId += 1;
    const old = recognition;
    detach(old);
    recognition = null;
    if (old) {
      try {
        old.stop();
      } catch (_e) {
        // ignore
      }
    }
    log("Stopped");
    onStatus("stopped");
  }

  return { start, stop, isRunning: () => running };
}
